import {ChangeDetectionStrategy, Component, Input, OnDestroy, OnInit} from '@angular/core'
import {AbstractControl} from "@angular/forms"
import {IQuestionComponent} from 'app/interfaces/IQuestionComponent'
import {SurveyViewerService} from '../../../services/survey-viewer.service'
import {Open} from 'app/models/questions/Open'
import {OpenStatementErrorMatcher} from 'app/error-matchers/OpenStatementErrorMatcher'
import {BehaviorSubject, fromEvent, Subject, takeUntil} from "rxjs";
import {IQuestionHistory, SkynetShieldService} from "@store/skynet-shield.service";
import {IValidationResponse, ValidateCommentsAIService} from "@store/validateCommentsAI.service";
import {InputPersistenceService} from '../../../services/input-persistence.service';

@Component({
  selector: 'app-question-open',
  templateUrl: './open.component.html',
  styleUrls: ['./open.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class OpenComponent implements IQuestionComponent, OnInit, OnDestroy {

  //
  // Track info (https://github.com/roundtableAI/alias-api/blob/main/tracking-script.js)
  //
  questionHistory$: BehaviorSubject<IQuestionHistory[]> = new BehaviorSubject([]);
  oldText: string;
  textOverLength = false;
  startTime: number;
  maxCharactersForHistory = 25000;
  private ngUnsubscribe = new Subject<void>();

  @Input() question: Open
  @Input() isSmallScreen: boolean
  @Input() activeLang: string

  isValidAI: BehaviorSubject<IValidationResponse> = new BehaviorSubject({isValid: true})

  questionFormControl: AbstractControl
  errorMatcher: OpenStatementErrorMatcher

  constructor(
    public surveyViewerService: SurveyViewerService,
    private skynetShieldService: SkynetShieldService,
    private validateAI: ValidateCommentsAIService,
    private inputPersistence: InputPersistenceService
  ) { }

  ngOnInit(): void {
    this.errorMatcher = new OpenStatementErrorMatcher(this.question.isOptional)

    this.questionHistory$.subscribe((history) => {
      // console.log(history);
      this.skynetShieldService.updateQuestionHistory(this.question.key, history);
    });

    const rowKey = this.question.rowKeys[this.question.rowKeys.length - 1];
    const statementKey = this.question.statementKeys[this.question.statementKeys.length - 1];

    this.questionFormControl
      = this.surveyViewerService.activeSurvey$.value.form
      .get(this.question.key)
      .get(rowKey)
      .get(this.question.commentsControlName)
      .get(statementKey)

    // Restaurer la valeur sauvegardée si elle existe (priorité à la valeur en cours de saisie)
    const savedValue = this.inputPersistence.getValue(this.question.key, statementKey, rowKey);
    if (savedValue) {
      this.questionFormControl.setValue(savedValue, {emitEvent: false});
    }

    this.surveyViewerService.changeFormUpdateStrategy(this.questionFormControl, 'blur');

    // Surveiller les changements du Survey pour restaurer la valeur quand le formulaire se recharge
    this.surveyViewerService.activeSurvey$.pipe(
      takeUntil(this.ngUnsubscribe)
    ).subscribe(survey => {
      if (survey) {
        const newFormControl = survey.form
          .get(this.question.key)
          ?.get(rowKey)
          ?.get(this.question.commentsControlName)
          ?.get(statementKey);
        
        if (newFormControl) {
          const currentValue = newFormControl.value;
          const savedValue = this.inputPersistence.getValue(this.question.key, statementKey, rowKey);
          
          if (savedValue && (!currentValue || currentValue === '')) {
            setTimeout(() => {
              newFormControl.setValue(savedValue, {emitEvent: false});
            }, 0);
          }
        }
      }
    });

    // ------------------------------ Tracking stuff ------------------------------
    // (juste les users publics)
    // if (!this._userService.user$.value?.isPublic) {
    //   return;
    // }
    this.questionFormControl.valueChanges.subscribe(value => {
      if (this.textOverLength) return;
      if (!this.startTime) {
        this.startTime = Date.now();
      }
      const t = Date.now() - this.startTime;
      const newHistory = {s: value, t};
      const lengthOfHistory = JSON.stringify([...this.questionHistory$.value, newHistory]).length;
      if (lengthOfHistory > this.maxCharactersForHistory) {
        this.textOverLength = true;
        return;
      }
      this.questionHistory$.next([...this.questionHistory$.value, newHistory]);
      this.oldText = value;
    });
    fromEvent(document, 'copy').pipe(
      takeUntil(this.ngUnsubscribe) // Assuming you have a Subject that emits when the component is destroyed
    ).subscribe((e: ClipboardEvent) => {
      if (this.textOverLength) return;
      const t = Date.now() - this.startTime;
      const newHistory = {s: this.questionFormControl.value, t, o: 'c', ct: e.clipboardData.getData('text')};
      const lengthOfHistory = JSON.stringify([...this.questionHistory$.value, newHistory]).length;
      if (lengthOfHistory > this.maxCharactersForHistory) {
        this.textOverLength = true;
        return;
      }
      this.questionHistory$.next([...this.questionHistory$.value, newHistory]);
    });
  }

  onBlur(value: string){
    this.questionFormControl.setValue(value);
    // Note: On ne nettoie PAS ici car le formulaire peut se recharger juste après
    // Le nettoyage se fera uniquement lors de la soumission finale du sondage

    if (!this.question.disableOpenStatementsRealtimeValidation) {
      this.validateAI.validateAnswerWithOpenAI(this.question.question[this.activeLang], value, this.activeLang)
        .then((response) => {
          this.isValidAI.next(response);
          this.validateAI.updateQuestionAIValidationStatus(this.question.key, response.isValid);
        });
    }
  }

  /**
   * Sauvegarde la valeur en temps réel (appelé à chaque frappe)
   */
  onInput(value: string){
    const rowKey = this.question.rowKeys[this.question.rowKeys.length - 1];
    const statementKey = this.question.statementKeys[this.question.statementKeys.length - 1];
    this.inputPersistence.saveValue(this.question.key, value, statementKey, rowKey);
  }

  ngOnDestroy(){
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

}
